import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FileBarChart, User, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', entity_type: '' });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      const response = await axios.get(`${API_URL}/audit`, { withCredentials: true, params });
      setLogs(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar auditoria');
    } finally {
      setLoading(false);
    }
  }, [filters.action, filters.entity_type]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const actionLabel = (action) => {
    const map = { create: 'Criação', update: 'Atualização', delete: 'Exclusão', status_change: 'Mudança de Status', login: 'Login' };
    return map[action] || action;
  };

  const entityLabel = (type) => {
    const map = { client: 'Cliente', product: 'Produto', template: 'Modelo', document: 'Documento', settings: 'Configurações', standard_text: 'Texto Padrão', requirement: 'Requisito', legal_base: 'Base Legal', price_tier: 'Faixa de Preço', user: 'Usuário' };
    return map[type] || type;
  };

  const actionColor = (action) => {
    const map = { create: 'text-green-600 bg-green-50', update: 'text-blue-600 bg-blue-50', delete: 'text-red-600 bg-red-50', status_change: 'text-yellow-600 bg-yellow-50', login: 'text-gray-600 bg-gray-50' };
    return map[action] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="text-3xl font-bold text-[#111111]">Auditoria</h1>
        <p className="text-[#6E6E73]">Registro de atividades do sistema</p>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Select value={filters.action} onValueChange={(val) => setFilters({ ...filters, action: val === 'all' ? '' : val })}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Filtrar por ação" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as ações</SelectItem>
              <SelectItem value="create">Criação</SelectItem>
              <SelectItem value="update">Atualização</SelectItem>
              <SelectItem value="delete">Exclusão</SelectItem>
              <SelectItem value="status_change">Mudança de Status</SelectItem>
              <SelectItem value="login">Login</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.entity_type} onValueChange={(val) => setFilters({ ...filters, entity_type: val === 'all' ? '' : val })}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Filtrar por entidade" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as entidades</SelectItem>
              <SelectItem value="client">Cliente</SelectItem>
              <SelectItem value="product">Produto</SelectItem>
              <SelectItem value="template">Modelo</SelectItem>
              <SelectItem value="document">Documento</SelectItem>
              <SelectItem value="user">Usuário</SelectItem>
              <SelectItem value="settings">Configurações</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadLogs} className="bg-[#AF2628] hover:bg-[#961B22]">
            <Search className="w-4 h-4 mr-2" />Filtrar
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>
        ) : logs.length > 0 ? (
          <div className="border border-[#E5E5EA] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F7F7F8]">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">DATA/HORA</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">USUÁRIO</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">AÇÃO</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">ENTIDADE</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={`${log.timestamp}-${log.user_id}-${i}`} className="border-t border-[#E5E5EA] hover:bg-[#F7F7F8]">
                    <td className="p-3 text-sm text-[#6E6E73]">{new Date(log.timestamp).toLocaleString('pt-BR')}</td>
                    <td className="p-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#AF2628] flex items-center justify-center text-white text-xs">
                          {(log.user_name || '?')[0]}
                        </div>
                        {log.user_name || log.user_id}
                      </div>
                    </td>
                    <td className="p-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${actionColor(log.action)}`}>
                        {actionLabel(log.action)}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{entityLabel(log.entity_type)}</td>
                    <td className="p-3 text-xs text-[#AEAEB2] font-mono">{log.entity_id?.slice(0, 20) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FileBarChart className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73]">Nenhum registro de auditoria encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditPage;
